// src/components/settings/SectorManager.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, Input, Switch, Typography, Tooltip, message } from 'antd';
import { PlusOutlined, EditOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
// ⬇️ usa il file "sector" (singolare) come da tua nota
import {
  listSectors,
  createSector,
  updateSector,
  type SectorDTO,
  type CreateSectorRequest,
} from '../../services/sector';

const { Text } = Typography;

type Row = SectorDTO & { _tmpId?: number };

const SectorManager: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState<number | string | null>(null);
  const [q, setQ] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const list = await listSectors();
      setRows((list || []).map((s: SectorDTO) => ({ ...s })));
    } catch (e: any) {
      message.error(e?.message || 'Errore nel caricamento settori');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const isEditing = (r: Row) => (r.id ?? r._tmpId) === editingKey;
  const startEdit = (r: Row) => setEditingKey(r.id ?? r._tmpId ?? `tmp-${Date.now()}`);
  const stopEdit = () => setEditingKey(null);

  const updateRow = (key: number | string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => ((r.id ?? r._tmpId) === key ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    const tmp: Row = { id: undefined as any, name: '', icon: '', active: true, _tmpId: Date.now() * -1 };
    setRows((prev) => [tmp, ...prev]);
    setEditingKey(tmp._tmpId!);
  };

  const saveChanges = async () => {
    try {
      setLoading(true);

      // CREATE (solo righe senza id e con name valorizzato)
      const toCreate = rows.filter((r) => !r.id && (r.name || '').trim());
      for (const r of toCreate) {
        const payload: CreateSectorRequest = {
          name: (r.name || '').trim(),
          icon: (r.icon || '').trim() || null,
          active: r.active ?? true,
        };
        await createSector(payload);
      }

      // UPDATE: confronta con il server e manda solo i cambi
      const server = await listSectors();
      const map = new Map<number, SectorDTO>();
      (server || []).forEach((s: SectorDTO) => s.id && map.set(s.id, s));

      const toUpdate = rows.filter((r) => r.id && hasChanged(r, map.get(r.id!)));
      for (const r of toUpdate) {
        // ⬇️ niente "id" nel body, lo passiamo nella URL
        const payload: CreateSectorRequest = {
          name: (r.name || '').trim(),
          icon: (r.icon || '').trim() || null,
          active: !!r.active,
        };
        await updateSector(r.id!, payload);
      }

      message.success('Settori salvati');
      await load();
      stopEdit();
    } catch (e: any) {
      message.error(e?.message || 'Errore salvataggio settori');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        (r.name || '').toLowerCase().includes(term) ||
        (r.icon || '').toLowerCase().includes(term)
    );
  }, [rows, q]);

  const columns: ColumnsType<Row> = [
 
    {
      title: 'Nome',
      dataIndex: 'name',
      width : "70%",
      render: (v: string, r) => {
        const key = r.id ?? r._tmpId!;
        return isEditing(r) ? (
          <Input
            size="small"
            placeholder="Es. Ristorazione"
            value={v || ''}
            onChange={(e) => updateRow(key, { name: e.target.value })}
          />
        ) : (
          <Text>{v}</Text>
        );
      },
    },
    {
      title: 'Attivo',
      dataIndex: 'active',
      align: 'center',
      width: "20%",
      render: (v: boolean, r) => {
        const key = r.id ?? r._tmpId!;
        return isEditing(r) ? (
          <Switch checked={!!v} onChange={(checked) => updateRow(key, { active: checked })} />
        ) : (
          <Switch checked={!!v} disabled />
        );
      },
    },
    {
      key: 'actions',
      width: "10%",
      align: 'right',
      render: (_: any, r) =>
        isEditing(r) ? (
          <Space>
            <Tooltip title="Salva">
              <Button icon={<SaveOutlined />} size="small" onClick={saveChanges} />
            </Tooltip>
          </Space>
        ) : (
          <Space>
            <Tooltip title="Modifica">
              <Button icon={<EditOutlined />} size="small" onClick={() => startEdit(r)} />
            </Tooltip>
          </Space>
        ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} onClick={addRow}>
          Aggiungi Settore
        </Button>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Cerca per nome"
          allowClear
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </Space>

      <Table<Row>
        size="small"
        rowKey={(r) => (r.id ?? r._tmpId)!}
        loading={loading}
        columns={columns}
        dataSource={filtered}
        pagination={false}
        scroll={{ x: 500 }}
      />
    </div>
  );
};

function hasChanged(next: SectorDTO, prev?: SectorDTO): boolean {
  if (!prev) return true;
  const norm = (s?: string | null) => (s || '').trim();
  return (
    norm(next.name) !== norm(prev.name) ||
    norm(next.icon) !== norm(prev.icon) ||
    (!!next.active) !== (!!prev.active)
  );
}

export default SectorManager;
